import { BLACK, WHITE, EMPTY } from './constants'
import {
  getLegalMoves,
  countPieces,
  getCornerMask,
  getXSquareMask,
  getCSquareMask,
} from './board'

/** 标准 8x8 位置权重；其他尺寸按比例映射到角/边/中心 */
const WEIGHT_8 = [
  [120, -20, 20, 5, 5, 20, -20, 120],
  [-20, -40, -5, -5, -5, -5, -40, -20],
  [20, -5, 15, 3, 3, 15, -5, 20],
  [5, -5, 3, 3, 3, 3, -5, 5],
  [5, -5, 3, 3, 3, 3, -5, 5],
  [20, -5, 15, 3, 3, 15, -5, 20],
  [-20, -40, -5, -5, -5, -5, -40, -20],
  [120, -20, 20, 5, 5, 20, -20, 120],
]

function positionWeight(size, r, c) {
  if (size === 8) return WEIGHT_8[r][c]
  const scale = (r / (size - 1)) * 7
  const scaleC = (c / (size - 1)) * 7
  const r0 = Math.floor(scale)
  const c0 = Math.floor(scaleC)
  const r1 = Math.min(7, r0 + 1)
  const c1 = Math.min(7, c0 + 1)
  const fr = scale - r0
  const fc = scaleC - c0
  const w =
    WEIGHT_8[r0][c0] * (1 - fr) * (1 - fc) +
    WEIGHT_8[r1][c0] * fr * (1 - fc) +
    WEIGHT_8[r0][c1] * (1 - fr) * fc +
    WEIGHT_8[r1][c1] * fr * fc
  return w
}

/**
 * 从 player 视角评估局面（越大越好）
 */
export function evaluate(board, size, player) {
  const opp = player === BLACK ? WHITE : BLACK
  const corner = getCornerMask(size)
  const xSq = getXSquareMask(size)
  const cSq = getCSquareMask(size)

  let mobility = 0
  let stability = 0
  let positional = 0
  let corners = 0
  let xPenalty = 0
  let cPenalty = 0
  let discs = 0

  const myMoves = getLegalMoves(board, size, player).length
  const oppMoves = getLegalMoves(board, size, opp).length
  mobility = (myMoves - oppMoves) * 8
  if (myMoves === 0 && oppMoves > 0) mobility -= 40
  if (oppMoves === 0 && myMoves > 0) mobility += 40

  const { black, white } = countPieces(board, size)
  const myCount = player === BLACK ? black : white
  const oppCount = player === BLACK ? white : black
  const total = black + white
  const fillRatio = total / (size * size)

  for (let r = 0; r < size; r++) {
    for (let c = 0; c < size; c++) {
      const cell = board[r][c]
      if (cell === EMPTY) continue
      const sign = cell === player ? 1 : -1
      const pw = positionWeight(size, r, c)
      positional += sign * pw

      if (corner[r][c]) {
        corners += sign * 800
        stability += sign * 200
      } else if (xSq[r][c]) {
        xPenalty += sign * -120
      } else if (cSq[r][c]) {
        cPenalty += sign * -60
      }

      if (isStableDisc(board, size, r, c, cell)) {
        stability += sign * 15
      }
    }
  }

  discs = myCount - oppCount
  let discScore = 0
  if (fillRatio > 0.55) {
    discScore = discs * 12
  } else if (fillRatio > 0.35) {
    discScore = discs * 3
  } else {
    discScore = -Math.abs(discs) * 2
  }

  return (
    corners +
    stability +
    positional +
    mobility +
    xPenalty +
    cPenalty +
    discScore
  )
}

function isStableDisc(board, size, row, col, color) {
  if (board[row][col] !== color) return false
  const corner = getCornerMask(size)
  if (corner[row][col]) return true

  const edges = []
  for (let c = 0; c < size; c++) {
    if (board[0][c] === color) edges.push([0, c])
    if (board[size - 1][c] === color) edges.push([size - 1, c])
  }
  for (let r = 1; r < size - 1; r++) {
    if (board[r][0] === color) edges.push([r, 0])
    if (board[r][size - 1] === color) edges.push([r, size - 1])
  }

  for (const [er, ec] of edges) {
    if (er === row && ec === col) {
      if (isEdgeStable(board, size, row, col, color)) return true
    }
  }
  return false
}

function isEdgeStable(board, size, row, col, color) {
  const onTop = row === 0
  const onBottom = row === size - 1
  const onLeft = col === 0
  const onRight = col === size - 1
  if (!(onTop || onBottom || onLeft || onRight)) return false

  if (board[row][col] !== color) return false

  if (onTop && board[0][0] === color && col <= size / 2) {
    for (let c = col; c >= 0; c--) if (board[0][c] !== color) return false
    return true
  }
  if (onTop && board[0][size - 1] === color && col >= size / 2) {
    for (let c = col; c < size; c++) if (board[0][c] !== color) return false
    return true
  }
  return false
}

export function evaluateTerminal(board, size, player) {
  const { black, white } = countPieces(board, size)
  if (black === white) return 0
  const won = player === BLACK ? black > white : white > black
  return won ? 100000 : -100000
}